import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';

interface ChatLoaderBarProps {
  visible: boolean;
  label?: string;
  longWaitLabel?: string;
  longWaitThresholdSeconds?: number;
}

const DotsLoader: React.FC = () => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          backgroundColor: 'primary.main',
          animation: 'aihub-dot-pulse 1.3s ease-in-out infinite',
          animationDelay: `${i * 0.18}s`,
          '@keyframes aihub-dot-pulse': {
            '0%, 70%, 100%': { opacity: 0.25, transform: 'scale(0.85)' },
            '35%': { opacity: 1, transform: 'scale(1)' },
          },
        }}
      />
    ))}
  </Box>
);

export const ChatLoaderBar: React.FC<ChatLoaderBarProps> = ({
  visible,
  label = 'Generating response…',
  longWaitLabel = 'Still working — complex questions can take a minute',
  longWaitThresholdSeconds = 25,
}) => {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    if (!visible) {
      setElapsedSeconds(0);
      return;
    }
    const startedAt = Date.now();
    setElapsedSeconds(0);
    const id = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, [visible]);

  if (!visible) return null;

  const currentLabel =
    elapsedSeconds >= longWaitThresholdSeconds ? longWaitLabel : label;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        px: 2,
        py: 0.75,
      }}
    >
      <DotsLoader />
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {/* Only the label is in a live region — the seconds counter is
            decorative and would otherwise make screen readers chatter
            every second ("...1s, 2s, 3s..."). */}
        <span role="status" aria-live="polite">{currentLabel}</span>{' '}
        <span aria-hidden="true">{elapsedSeconds}s</span>
      </Typography>
    </Box>
  );
};

export default ChatLoaderBar;
